import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ExceptionMatrixTab() {
  const [rules, setRules] = useState(null);
  const [levels, setLevels] = useState([]);
  const [version, setVersion] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/policy/exception-matrix").then(r => {
      setRules(r.data.rules); setLevels(r.data.approval_levels); setVersion(r.data.version);
    }).catch(() => toast.error("Could not load exception matrix"));
  }, []);

  if (!rules) return <div className="text-sm text-slate-500">Loading exception matrix…</div>;

  const setLevel = (i, level) => setRules(rs => rs.map((r, idx) => (idx === i ? { ...r, level } : r)));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/policy/exception-matrix", { rules, reason });
      setRules(data.rules); setVersion(data.version); setReason("");
      toast.success(`Exception matrix saved — v${data.version}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="exception-matrix-tab">
      <Card className="border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">
          Exception triggers → required approval authority <span className="text-xs font-normal text-slate-500">v{version}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-2">Trigger</th><th className="text-left px-4 py-2">Description</th><th className="text-left px-4 py-2 w-72">Approval level</th></tr>
          </thead>
          <tbody>
            {rules.map((r, i) => (
              <tr key={r.trigger} className="border-t border-slate-100" data-testid={`exception-row-${r.trigger}`}>
                <td className="px-4 py-2 font-mono text-xs">{r.trigger}</td>
                <td className="px-4 py-2 text-slate-700">{r.description}</td>
                <td className="px-4 py-2">
                  <Select value={r.level} onValueChange={v => setLevel(i, v)}>
                    <SelectTrigger className="h-8" data-testid={`exception-level-${r.trigger}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{levels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card className="p-5 border-teal-200 bg-teal-50/50">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-64">
            <Label className="text-xs">Reason for change</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} className="mt-1 bg-white" data-testid="exception-reason" />
          </div>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="exception-save-btn">Save matrix</Button>
        </div>
      </Card>
    </div>
  );
}
